import React, { Children, useMemo, useRef } from 'react'
import { CardFullHeightScrollY } from '../card/card-full-height-scroll-y'
import { Menu, MenuExport, MenuTableSettingToggle } from '../menu/menu'
import { MenuHeaderContext } from '../menu/menu-context'
import { TableToolbarContext } from '../table-builder/table-toolbar'
import { SelectionNotice, selectModeMenu } from '../table-builder/select-mode'
import './fleet.scss'

// A list on a fleet / SSO page is the table-builder's AG Grid (TableAgClient
// or TableAgInfinite), exactly as on every other list page: the same card,
// the same header menu (export, table settings), the same quick filter, grid
// and "reload | fit to columns | fit to page / Pagination / Rows" footer.
//
// The one difference is height. A list page's card fills a fixed-height
// frame; a fleet page is a pane that scrolls itself (.sw-pane), with summary
// tiles and filters above the list, so there is no parent height to fill and
// an auto-height box would collapse the grid to nothing. The card sits in a
// box of its own height instead: .sw-grid for a page's main list,
// .sw-grid-compact for a short list inside a section or panel.
//
// A compact grid is sized as the card's chrome plus five rows (fleet.scss),
// so it shows five rows before it scrolls. rowHeight: a grid whose rows are
// taller than the theme's (the same value handed to its TableAgClient, e.g.
// ROW_HEIGHT_TWO_LINE) passes it here too, and the five rows use it
// (--sw-grid-row-h).
//
// phoneTall: a compact grid that is taller on a phone (30rem below 576 px),
// where its card's chrome wraps; opt-in (the audit screens' compact grids).
//
// rows: a list that is often short (owner 2026-10-30, the alert connections:
// "ONE row in a huge empty box") sizes its card to its rows instead: the same
// chrome plus that many rows of its row height -- at least FIT_EMPTY_ROWS when
// there are none, so the empty state has room -- and never more than the usual
// height (five rows for a compact grid, the page's height for a page's list).
// Hand it the number of rows the list holds (0 while it loads).
//
// tableRef is the grid's ref (TableAgClient/TableAgInfinite forward
// exportCsv / toggleSideBar through it). exportName names the CSV the grid
// exports; leave it out for a list with no export. menu is extra header
// items (MenuItem elements, as an array), shown before export.
//
// flush: a grid inside a section (FleetSection, WorkspaceSection). The section
// is the one container and its heading names the list, so the grid gets no
// card of its own -- no second frame, no inner heading (title, if given, only
// labels the group for assistive tech). The menu moves into the table's
// toolbar row, after the quick filter (TableToolbarContext); toolbar is the
// page's own controls for that row, before the filter (e.g. a status select).
//
// tableSettings={false}: a list whose columns are the page's own choice offers
// no Table settings at all (owner 2026-10-29, the AI tools telemetry lists).
//
// menu is the list's actions as the shared menu's items (owner 2026-10-29:
// "use our menu system like everywhere else"): an item marked `pin` is a
// button in the header (`keepOnMobile` keeps it a button on a phone), every
// other item -- export, Table settings -- is in the ⋮, and Table settings is
// never a button of its own. A flush grid has the same menu in its toolbar
// row. notice: a status line under the header (what an action is doing or
// last said: a running Download, its result).
//
// selection, selectActions: select mode (table-builder/grid-selection.js and
// select-mode.js; owner 2026-10-29: selecting rows, select all and deselect
// all are the shared table's, never a page's own). "Select" is a pinned item
// of the menu; while selecting, the menu's pinned items are the page's
// selection actions (selectActions, MenuItems) and Done, and the status line
// says "12 selected · Select all 340 matching · Deselect all" before the
// page's notice. The same selection goes to the grid inside (TableAgInfinite
// / TableAgClient `selection`), which draws the checkbox column.
//
// actions: older pages' own buttons in the card's header, beside the menu
// (owner 2026-10-23); a page moves them into menu as pinned items. The house
// list page: compact filters in a .sw-toolbar row above the card, the list's
// actions in its menu, a link to another page in FleetPage's actions
// (CLAUDE.md, "List page layout").
export const FIT_EMPTY_ROWS = 3
export const COMPACT_ROWS = 5
export const fitRows = (rows, compact) => {
  const n = Math.max(0, Math.floor(Number(rows) || 0))
  const shown = n === 0 ? FIT_EMPTY_ROWS : n
  return compact ? Math.min(COMPACT_ROWS, shown) : shown
}

export const FleetGrid = ({ title, subtitle, tableRef, exportName, menu: pageMenu, tableSettings = true, compact, rowHeight, phoneTall, rows, flush, toolbar, actions, notice: pageNotice, selection, selectActions, testId, children }) => {
  const menu = useMemo(() => (selection ? selectModeMenu({ selection, menu: pageMenu, actions: selectActions }) : pageMenu), [selection, pageMenu, selectActions])
  const notice = selection && selection.selecting ? <><SelectionNotice selection={selection} />{pageNotice}</> : pageNotice

  // The card header draws HeaderMenu as a component, so it is one component
  // for the grid's life that reads the latest items: a new one on every
  // change of the items (a page builds them on each render) remounted the
  // menu, and an open ⋮ closed under the pointer whenever the page drew
  // again (a count arriving, a selection, a status line).
  const latestMenu = useRef()
  latestMenu.current = { menu, tableRef, exportName, tableSettings }
  const HeaderMenu = useMemo(() => function GridHeaderMenu () {
    const now = latestMenu.current
    return <GridMenu menu={now.menu} tableRef={now.tableRef} exportName={now.exportName} tableSettings={now.tableSettings} />
  }, [])

  // flush: the same menu, in the table's toolbar row (styled as a header menu).
  const toolbarSlots = useMemo(
    () => (flush
      ? { start: toolbar, end: <>{actions && <div className="card-header-actions" data-testid="card-header-actions">{actions}</div>}<MenuHeaderContext.Provider value={true}><GridMenu menu={menu} tableRef={tableRef} exportName={exportName} tableSettings={tableSettings} /></MenuHeaderContext.Provider></> }
      : null),
    [flush, toolbar, actions, menu, tableRef, exportName, tableSettings]
  )

  const fit = rows !== undefined && rows !== null
  const className = `${compact ? (rowHeight ? 'sw-grid sw-grid-compact sw-grid-rows' : 'sw-grid sw-grid-compact') : 'sw-grid'}${compact && phoneTall ? ' sw-grid-phone-tall' : ''}${flush ? ' sw-grid-flush' : ''}${fit ? ' sw-grid-fit' : ''}`
  const sized = (compact || fit) && rowHeight ? { '--sw-grid-row-h': `${rowHeight}px` } : {}
  const counted = fit ? { '--sw-grid-rows': String(fitRows(rows, compact)) } : {}
  const style = Object.keys(sized).length || Object.keys(counted).length ? { ...sized, ...counted } : undefined

  if (flush) {
    return (
      <div className={className} style={style} role={title ? 'group' : undefined} aria-label={title || undefined} data-testid={testId}>
        <TableToolbarContext.Provider value={toolbarSlots}>
          {subtitle && <p className="sw-hint sw-grid-sub">{subtitle}</p>}
          {notice && <div className="card-notice" data-testid="card-notice">{notice}</div>}
          <div className="sw-grid-body">{children}</div>
        </TableToolbarContext.Provider>
      </div>
    )
  }

  return (
    <div className={className} style={style} data-testid={testId}>
      <CardFullHeightScrollY title={title} subtitle={subtitle} HeaderMenu={HeaderMenu} headerActions={actions} notice={notice}>
        {children}
      </CardFullHeightScrollY>
    </div>
  )
}

// A grid's menu: the page's own items, export (when the grid exports) and
// table settings. With no pinned item shown, everything is in the ⋮: a lone
// item would otherwise be drawn as a button ("Table Settings").
const pinnedShown = menu => Children.toArray(menu).some(item => item && item.props && item.props.show && item.props.pin)
const GridMenu = ({ menu, tableRef, exportName, tableSettings }) =>
  <Menu forceDropdown={!pinnedShown(menu)}>
    {menu}
    <MenuExport onClick={() => tableRef.current && tableRef.current.exportCsv(exportName)} show={Boolean(exportName)} />
    <MenuTableSettingToggle onClick={() => tableRef.current && tableRef.current.toggleSideBar()} show={tableSettings} />
  </Menu>

// The shape TableAgClient reads (useApiList's items / loaded / loading /
// error) for a page that loads its rows itself: undefined while loading,
// null when the load failed, else the rows. Memoised on the rows, so the
// grid is only handed new row data when the rows actually change.
export const useGridList = rows =>
  useMemo(() => {
    if (rows === undefined) return { items: [], loaded: false, loading: true, error: undefined }
    if (rows === null) return { items: [], loaded: true, loading: false, error: 'failed' }
    return { items: rows, loaded: true, loading: false, error: undefined }
  }, [rows])
